import {
  Badge,
  Box,
  Button,
  Flex,
  Input,
  Link,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import {
  createFileRoute,
  getRouteApi,
  useNavigate,
} from "@tanstack/react-router"
import { useEffect, useMemo, useState } from "react"
import { FiDownload } from "react-icons/fi"
import Select from "@/components/Common/AppSelect"
import { MmpDiscrepancyReportApi } from "../../../../api/mmpDiscrepancyReport"
import {
  type MmpDiscrepancyReportRowDTO,
  TransferService,
} from "../../../../client"
import { PaginationFooter } from "../../../../components/Common/PaginationFooter"
import DocumentStatusBadge from "../../../../components/WMS/shared/DocumentStatusBadge"
import { WmsListToolbar } from "../../../../components/WMS/shared/WmsListToolbar"
import { WmsPageHeader } from "../../../../components/WMS/shared/WmsPageHeader"
import {
  WmsPagePanel,
  WmsPageShell,
} from "../../../../components/WMS/shared/WmsPageShell"
import { WmsTableListCard } from "../../../../components/WMS/shared/WmsTableListCard"
import { getLicenseErrorDetails } from "../../../../utils/licenseErrors"

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const

export const Route = createFileRoute("/_layout/WMS/reports/mmp-discrepancies")({
  validateSearch: (search: Record<string, unknown>) => {
    const rawPage =
      typeof search.page === "number" ? search.page : Number(search.page ?? 1)
    const rawPageSize =
      typeof search.page_size === "number"
        ? search.page_size
        : Number(search.page_size ?? 50)
    const normalizedPageSize = PAGE_SIZE_OPTIONS.includes(
      rawPageSize as (typeof PAGE_SIZE_OPTIONS)[number],
    )
      ? rawPageSize
      : 50

    return {
      page: Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1,
      page_size: normalizedPageSize,
    }
  },
  component: MmpDiscrepanciesReportPage,
})

const mmpDiscrepanciesRouteApi = getRouteApi(
  "/_layout/WMS/reports/mmp-discrepancies",
)

function MmpDiscrepanciesReportPage() {
  const navigate = useNavigate({ from: "/WMS/reports/mmp-discrepancies" })
  const search = mmpDiscrepanciesRouteApi.useSearch() as {
    page: number
    page_size: number
  }
  const cardBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const theadBg = useColorModeValue("gray.100", "whiteAlpha.100")
  const zebraBg = useColorModeValue("blackAlpha.50", "whiteAlpha.50")
  const mutedText = useColorModeValue("gray.500", "gray.400")
  const page = search.page > 0 ? search.page : 1
  const pageSize = search.page_size
  const now = new Date()
  const defaultDateTo = now.toISOString().slice(0, 10)
  const defaultDateFrom = new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .slice(0, 10)
  const [dateFrom, setDateFrom] = useState(defaultDateFrom)
  const [dateTo, setDateTo] = useState(defaultDateTo)
  const [status, setStatus] = useState("")
  const [reason, setReason] = useState("")
  const [warehouseId, setWarehouseId] = useState("")
  const [isExporting, setIsExporting] = useState(false)

  const reportQuery = useQuery({
    queryKey: [
      "wms",
      "reports",
      "mmp-discrepancies",
      { dateFrom, dateTo, status, reason, warehouseId, page, pageSize },
    ],
    retry: false,
    queryFn: () =>
      TransferService.getMmpDiscrepancyReport(
        undefined,
        undefined,
        dateFrom || undefined,
        dateTo || undefined,
        status || undefined,
        reason || undefined,
        warehouseId || undefined,
        page,
        pageSize,
      ),
  })

  const warehouseOptions = useMemo(() => {
    const map = new Map<string, string>()
    ;(reportQuery.data?.items ?? []).forEach((row) => {
      if (row.source_warehouse_id && row.source_warehouse) {
        map.set(row.source_warehouse_id, row.source_warehouse)
      }
      if (row.target_warehouse_id && row.target_warehouse) {
        map.set(row.target_warehouse_id, row.target_warehouse)
      }
    })
    return Array.from(map.entries()).map(([id, code]) => ({ id, code }))
  }, [reportQuery.data])

  const rows = reportQuery.data?.items ?? []
  const total = reportQuery.data?.total ?? 0
  const licenseError = reportQuery.isError
    ? getLicenseErrorDetails(reportQuery.error)
    : null
  const statusCode =
    typeof reportQuery.error === "object" &&
    reportQuery.error !== null &&
    "status" in reportQuery.error
      ? Number(reportQuery.error.status)
      : null
  const reportErrorMessage =
    statusCode === 429
      ? "Zbyt wiele żądań do raportu. Poczekaj kilka sekund i spróbuj ponownie."
      : (licenseError?.description ??
        "Nie udało się załadować raportu rozbieżności MMP.")

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  useEffect(() => {
    if (page <= totalPages) return
    navigate({
      to: "/WMS/reports/mmp-discrepancies",
      search: {
        page: totalPages,
        page_size: pageSize,
      },
      replace: true,
    })
  }, [navigate, page, pageSize, totalPages])

  const updateSearch = (patch: Partial<typeof search>) => {
    navigate({
      to: "/WMS/reports/mmp-discrepancies",
      search: {
        page: patch.page ?? search.page,
        page_size: patch.page_size ?? search.page_size,
      },
    })
  }

  const handleExportCsv = async () => {
    try {
      setIsExporting(true)
      const { blob, fileName } = await MmpDiscrepancyReportApi.downloadCsv({
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        status: status || undefined,
        reason: reason || undefined,
        warehouseId: warehouseId || undefined,
      })
      const objectUrl = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = objectUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl)
      }, 60_000)
    } catch (error) {
      console.error("Failed to export MMP discrepancy report", error)
      window.alert(
        error instanceof Error
          ? error.message
          : "Nie udało się wyeksportować raportu CSV.",
      )
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <WmsPageShell>
      <WmsPageHeader
        title="Raport rozbieżności MMP"
        description="Operacyjna kontrola uszkodzeń, braków i odrzuconych pozycji dla przyjęć międzymagazynowych z eksportem do CSV."
        gradient="linear(to-r, orange.500, red.500)"
        badge="Reports / MMP"
      />

      <WmsPagePanel bg={cardBg} borderColor={panelBorder}>
        <WmsListToolbar
          filters={
            <>
              <Input
                size="sm"
                type="date"
                maxW={{ base: "100%", lg: "180px" }}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                borderRadius="xl"
              />
              <Input
                size="sm"
                type="date"
                maxW={{ base: "100%", lg: "180px" }}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                borderRadius="xl"
              />
              <Select
                size="sm"
                maxW={{ base: "100%", lg: "220px" }}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                borderRadius="xl"
              >
                <option value="">Wszystkie statusy</option>
                <option value="confirmed">confirmed</option>
                <option value="partially_confirmed">partially_confirmed</option>
              </Select>
              <Select
                size="sm"
                maxW={{ base: "100%", lg: "220px" }}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                borderRadius="xl"
              >
                <option value="">Wszystkie przyczyny</option>
                <option value="damage">Uszkodzenie</option>
                <option value="shortage">Brak ilości</option>
                <option value="wrong_item">Nieprawidłowy towar</option>
                <option value="wrong_qty">Nieprawidłowa ilość</option>
                <option value="packaging_issue">Problem z opakowaniem</option>
                <option value="other">Inne</option>
              </Select>
              <Select
                size="sm"
                maxW={{ base: "100%", lg: "240px" }}
                value={warehouseId}
                onChange={(e) => setWarehouseId(e.target.value)}
                borderRadius="xl"
              >
                <option value="">Wszystkie magazyny</option>
                {warehouseOptions.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.code}
                  </option>
                ))}
              </Select>
              <Select
                size="sm"
                maxW={{ base: "100%", lg: "170px" }}
                value={String(pageSize)}
                onChange={(event) =>
                  updateSearch({
                    page_size: Number(event.target.value),
                    page: 1,
                  })
                }
                borderRadius="xl"
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size} / strona
                  </option>
                ))}
              </Select>
            </>
          }
          actions={
            <>
              <Badge colorScheme="orange" borderRadius="full" px={3} py={1}>
                Dokumenty: {reportQuery.data?.affected_docs ?? 0}
              </Badge>
              <Badge colorScheme="red" borderRadius="full" px={3} py={1}>
                Pozycje: {reportQuery.data?.affected_rows ?? 0}
              </Badge>
              <Button
                size="sm"
                colorScheme="orange"
                leftIcon={<FiDownload />}
                onClick={handleExportCsv}
                isLoading={isExporting}
                loadingText="Eksport..."
                borderRadius="xl"
              >
                Eksport CSV
              </Button>
            </>
          }
        />

        <Box flex="1" minH={0} overflow="hidden">
          <WmsTableListCard
            isLoading={reportQuery.isPending}
            error={reportQuery.isError ? reportQuery.error : undefined}
            errorMessage={reportErrorMessage}
            isEmpty={
              !reportQuery.isPending &&
              !reportQuery.isError &&
              rows.length === 0
            }
            emptyMessage="Brak rozbieżności dla wybranych filtrów."
            minEmptyHeight="34vh"
            footer={
              total > 0 ? (
                <Flex
                  justify="space-between"
                  align={{ base: "stretch", md: "center" }}
                  direction={{ base: "column", md: "row" }}
                  gap={3}
                  px={4}
                  py={4}
                  flexShrink={0}
                >
                  <Text fontSize="sm" color={mutedText}>
                    Pokazano {(page - 1) * pageSize + 1}-
                    {(page - 1) * pageSize + rows.length} z {total}
                  </Text>
                  <PaginationFooter
                    mt={0}
                    onChangePage={(nextPage) =>
                      updateSearch({ page: nextPage })
                    }
                    page={page}
                    hasNextPage={page < totalPages}
                    hasPreviousPage={page > 1}
                  />
                </Flex>
              ) : undefined
            }
          >
            <TableContainer flex="1" minH={0} overflowY="auto">
              <Table size="sm" sx={{ tableLayout: "fixed" }}>
                <Thead bg={theadBg} position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <Th w="120px">MMP</Th>
                    <Th w="150px">Status</Th>
                    <Th w="120px">MMR</Th>
                    <Th w={{ base: "220px", xl: "280px" }}>Produkt</Th>
                    <Th w="90px" isNumeric>
                      Plan
                    </Th>
                    <Th w="90px" isNumeric>
                      Uszk.
                    </Th>
                    <Th w="90px" isNumeric>
                      Zwrot
                    </Th>
                    <Th w="150px">Przyczyna</Th>
                    <Th w={{ base: "220px", xl: "280px" }}>Komentarz</Th>
                    <Th w="180px">Trasa</Th>
                    <Th w="170px">Potwierdzono</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {rows.map((row: MmpDiscrepancyReportRowDTO, idx) => (
                    <Tr
                      key={`${row.mmp_id}-${row.sku}-${row.discrepancy_comment}`}
                      bg={idx % 2 === 0 ? "transparent" : zebraBg}
                    >
                      <Td whiteSpace="nowrap">
                        <Link
                          href={`/WMS/documents/doc-mmp/${row.mmp_id}`}
                          color="green.600"
                        >
                          {row.mmp_number}
                        </Link>
                      </Td>
                      <Td whiteSpace="nowrap">
                        <DocumentStatusBadge
                          status={row.mmp_status}
                          fontSize="sm"
                        />
                      </Td>
                      <Td whiteSpace="nowrap">
                        {row.mmr_id && row.mmr_number ? (
                          <Link
                            href={`/WMS/documents/doc-mmr/${row.mmr_id}`}
                            color="green.600"
                          >
                            {row.mmr_number}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </Td>
                      <Td>
                        <Stack spacing={1}>
                          <Text noOfLines={2}>{row.product_name || "—"}</Text>
                          <Text
                            fontSize="xs"
                            color="gray.500"
                            whiteSpace="nowrap"
                            overflow="hidden"
                            textOverflow="ellipsis"
                          >
                            {row.sku || "—"}
                          </Text>
                        </Stack>
                      </Td>
                      <Td isNumeric whiteSpace="nowrap">
                        {row.planned_qty.toFixed(2)}
                      </Td>
                      <Td isNumeric whiteSpace="nowrap">
                        {row.damaged_qty > 0 ? (
                          <Badge colorScheme="orange">
                            {row.damaged_qty.toFixed(2)}
                          </Badge>
                        ) : (
                          "—"
                        )}
                      </Td>
                      <Td isNumeric whiteSpace="nowrap">
                        {row.rejected_qty > 0 ? (
                          <Badge colorScheme="red">
                            {row.rejected_qty.toFixed(2)}
                          </Badge>
                        ) : (
                          "—"
                        )}
                      </Td>
                      <Td>
                        <Text noOfLines={2}>
                          {row.discrepancy_reason || "—"}
                        </Text>
                      </Td>
                      <Td maxW="280px">
                        <Text noOfLines={3}>
                          {row.discrepancy_comment || "—"}
                        </Text>
                      </Td>
                      <Td>
                        <Text noOfLines={2}>
                          {row.source_warehouse} → {row.target_warehouse}
                        </Text>
                      </Td>
                      <Td whiteSpace="nowrap">
                        {row.confirmed_at
                          ? new Date(row.confirmed_at).toLocaleString()
                          : "—"}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </WmsTableListCard>
        </Box>
      </WmsPagePanel>
    </WmsPageShell>
  )
}
